import { useMemo } from 'react';
import { Box, Fade, Paper, Typography, useTheme } from '@material-ui/core';
import { FormSummaryQuestionTitle } from './FormSummaryQuestionTitle';
import { DateListDataPoint } from '@vyce/core/src/types';
import { FormSummaryDateAnswer } from './FormSummaryDateAnswer';
import { formatFormDateLabel, parseFormDate } from '../utils/formSummaryUtils';

interface DateGroup {
  year: string;
  month: string;
  monthName: string;
  days: Array<{ label: string; count: number; fullDate: string }>;
  description?: string;
  videoLink?: string;
  photoUrl?: string;
  pdfUrl?: string;
}

export const FormSummaryDate = ({
  title,
  totalAnswers,
  data,
  description,
  videoLink,
  photoUrl,
  explanation,
  pdfUrl,
  onItemClick,
}: {
  title: string;
  totalAnswers: number;
  data: DateListDataPoint[];
  description?: string;
  videoLink?: string;
  photoUrl?: string;
  explanation?: string;
  pdfUrl?: string;
  onItemClick?: (answerValue: string, answerLabel: string) => void;
}) => {
  const theme = useTheme();

  const groupedData = useMemo(() => {
    const dateCounts = new Map<string, number>();
    data.forEach(item => {
      const count = dateCounts.get(item.value as string) || 0;
      dateCounts.set(item.value as string, count + 1);
    });

    const yearMonthGroups = new Map<string, Map<string, number>>();
    dateCounts.forEach((count, dateValue) => {
      const date = parseFormDate(dateValue);
      if (!date.isValid()) return;
      const year = date.format('YYYY');
      const month = date.format('MM');
      const key = `${year}-${month}`;

      if (!yearMonthGroups.has(key)) {
        yearMonthGroups.set(key, new Map());
      }
      yearMonthGroups.get(key)!.set(dateValue, count);
    });

    const result: DateGroup[] = Array.from(yearMonthGroups.entries())
      .map(([key, dates]) => {
        const [year, month] = key.split('-');
        const date = parseFormDate(`${year}-${month}-01`);
        const monthName = date.format('MMMM');

        return {
          year,
          month,
          monthName,
          days: Array.from(dates.entries())
            .map(([fullDate, count]) => {
              const label = formatFormDateLabel(fullDate);
              return { label, count, fullDate };
            })
            .sort((a, b) => parseFormDate(a.fullDate).valueOf() - parseFormDate(b.fullDate).valueOf()),
        };
      })
      .sort((a, b) => {
        if (a.year !== b.year) {
          return a.year.localeCompare(b.year);
        }
        return a.month.localeCompare(b.month);
      });

    return result;
  }, [data]);

  return (
    <Fade in={true} timeout={200} style={{ width: '100%' }}>
      <Paper variant="outlined" style={{ padding: 24, width: '100%' }}>
        <FormSummaryQuestionTitle
          title={title}
          totalAnswers={totalAnswers}
          description={description}
          videoLink={videoLink}
          photoUrl={photoUrl}
          explanation={explanation}
          pdfUrl={pdfUrl}
        />

        <Box marginTop={2}>
          {groupedData.map((group, groupIndex) => (
            <Box
              key={`${group.year}-${group.month}`}
              style={{
                borderBottom:
                  groupIndex < groupedData.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                paddingBottom: groupIndex < groupedData.length - 1 ? 16 : 0,
                paddingTop: groupIndex > 0 ? 16 : 0,
              }}
            >
              <Box display="flex" alignItems="center" style={{ gap: 16 }}>
                <Box style={{ minWidth: 150 }}>
                  <Typography
                    variant="caption"
                    style={{ fontWeight: 500, color: theme.palette.text.secondary }}
                  >
                    {group.year}
                  </Typography>
                  <Typography variant="body2" style={{ fontWeight: 500, color: theme.palette.text.primary }}>
                    {group.monthName}
                  </Typography>
                </Box>

                <Box display="flex" flexWrap="wrap" style={{ gap: 8, flex: 1 }}>
                  {group.days.map(({ label, count, fullDate }) => (
                    <Box key={fullDate}>
                      <FormSummaryDateAnswer
                        value={label}
                        count={count}
                        onClick={
                          onItemClick
                            ? () => onItemClick(fullDate, formatFormDateLabel(fullDate))
                            : undefined
                        }
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Paper>
    </Fade>
  );
};
